import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useFetchData from "repo/get";
import EditHoursDialog from "./EditDialog";
import ExportPontos from "./ExportPontos";

const DataTablePontos = ({ id }) => {
    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [ano, setAno] = useState(2025);
    const [selectedData, setSelectedData] = useState(null);
    const { data, loading, error } = useFetchData(`api/pontos/findByMesAno/${ano}/${mes}/${id.id}`);

    useEffect(() => {

    }, [error]);

    const validData = Array.isArray(data) ? data : [];

    const getDayOfWeek = (dateStr) => {
        const [day, month, year] = dateStr.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        return date.getDay();
    };

    const feriados = [
        "01/01/2025",
        "03/03/2025",
        "04/03/2025",
        "05/03/2025",
        "18/04/2025",
        "21/04/2025",
        "01/05/2025",
        "19/06/2025",
        "07/09/2025",
        "12/10/2025",
        "28/10/2025",
        "02/11/2025",
        "15/11/2025",
        "20/11/2025",
        "24/12/2025",
        "25/12/2025",
        "31/12/2025"
    ];

    const handleRowClick = (rowData) => {
        if (!rowData.id || Object.keys(rowData).length === 0) return;
        setSelectedData(rowData);
    };

    const handleSave = (updatedData) => {
        setSelectedData(null);
        const monthStr = updatedData.date.split("-")[1];
        const month = parseInt(monthStr, 10);
        handleMonthChange(month + 1);
        handleMonthChange(month);
    };

    const handleCancel = () => {
        setSelectedData(null);
    };

    const isFeriado = (dateStr) => feriados.includes(dateStr);

    const rowClassName = (rowData) => {
        const dayOfWeek = getDayOfWeek(rowData.date);
        if (isFeriado(rowData.date)) {
            return 'holiday-row';
        }
        if (dayOfWeek === 0) {
            return 'sunday-row';
        }
        if (dayOfWeek === 6) {
            return 'saturday-row';
        }
        return '';
    };

    const generateDates = () => {
        const daysInMonth = new Date(ano, mes, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, index) => {
            const day = index + 1;
            return {
                date: `${day < 10 ? '0' + day : day}/${mes < 10 ? '0' + mes : mes}/${ano}`,
                hour_first: '',
                hour_dinner: '',
                hour_stop: '',
                hour_finish: ''
            };
        });
    };

    const calculateWorkedHours = (start, dinner, stop, finish) => {
        if (!start || !dinner || !stop || !finish) return 0;

        const parseTime = (timeStr) => {
            const [hour, minute] = timeStr.split(':').map(Number);
            if (isNaN(hour) || isNaN(minute)) return null;
            return new Date(2025, 0, 1, hour, minute);
        };

        const startTime = parseTime(start);
        let dinnerTime = parseTime(dinner);
        let stopTime = parseTime(stop);
        let finishTime = parseTime(finish);

        if (!startTime || !dinnerTime || !stopTime || !finishTime) return 0;


        const adjustTime = (time, reference) => {
            if (time < reference) {
                return new Date(time.getTime() + 86400000);
            }
            return time;
        };

        dinnerTime = adjustTime(dinnerTime, startTime);
        stopTime = adjustTime(stopTime, dinnerTime);
        finishTime = adjustTime(finishTime, stopTime);

        const workBeforeLunch = dinnerTime - startTime;
        const workAfterBreak = finishTime - stopTime;

        return Math.max(workBeforeLunch + workAfterBreak, 0);
    };

    const convertMillisecondsToHours = (ms) => {
        if (isNaN(ms) || ms <= 0) return "00h 00m";

        const hours = Math.floor(ms / 3600000);
        const minutes = Math.floor((ms % 3600000) / 60000);

        return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
    };

    const calculateTotalWorkedMilliseconds = (dataWithDates) => {
        return dataWithDates.reduce((total, data) => {
            return total + calculateWorkedHours(
                data.hour_first,
                data.hour_dinner,
                data.hour_stop,
                data.hour_finish
            );
        }, 0);
    };

    const calculateTotalWorkedHours = (dataWithDates) => {
        const totalMs = calculateTotalWorkedMilliseconds(dataWithDates);
        return convertMillisecondsToHours(totalMs);
    };

    const WorkedHoursExtraMilliseconds = (dataWithDates) => {
        const totalWorkedMilliseconds = calculateTotalWorkedMilliseconds(dataWithDates);
        const regularHours = 220 * 3600000;
        const extraMilliseconds = totalWorkedMilliseconds - regularHours;
        return Math.max(extraMilliseconds, 0);
    };

    const WorkedHoursExtraFeriasosMilliseconds = (dataWithDates) => {
        let extraMilliseconds = 0;

        dataWithDates.forEach((data) => {
            const { hour_first, hour_dinner, hour_stop, hour_finish, date } = data;
            const workedHours = calculateWorkedHours(hour_first, hour_dinner, hour_stop, hour_finish);
            const dayOfWeek = getDayOfWeek(date);
            const isSunday = dayOfWeek === 0;
            const isHoliday = isFeriado(date);

            if (isSunday || isHoliday) {
                extraMilliseconds += workedHours;
            }
        });

        return Math.max(extraMilliseconds, 0);
    };

    const calculateExtraNormal = (dataWithDates) => {
        const extraMs = WorkedHoursExtraMilliseconds(dataWithDates);
        return convertMillisecondsToHours(extraMs);
    };

    const calculateExtraFeriados = (dataWithDates) => {
        const extraMs = WorkedHoursExtraFeriasosMilliseconds(dataWithDates);
        return convertMillisecondsToHours(extraMs);
    };

    const calculateDaysOff = (dataWithDates) => {
        const extraMs = WorkedHoursExtraMilliseconds(dataWithDates);
        return Math.floor(extraMs / (3600000 * 8));
    };

    const combineData = (dates) => {
        return dates.map((dateData) => {
            const date = dateData.date;

            const matchingData = validData.find((apiData) => {
                const apiDate = apiData.date.replace(/-/g, '/');
                return apiDate === date;
            });

            if (matchingData) {
                const workedHours = convertMillisecondsToHours(calculateWorkedHours(
                    matchingData.hour_first,
                    matchingData.hour_dinner,
                    matchingData.hour_stop,
                    matchingData.hour_finish
                ));
                return {
                    ...dateData,
                    data: matchingData,
                    id: matchingData.id,
                    hour_first: matchingData.hour_first || '',
                    hour_dinner: matchingData.hour_dinner || '',
                    hour_stop: matchingData.hour_stop || '',
                    hour_finish: matchingData.hour_finish || '',
                    worked_hours: workedHours
                };
            }
            return dateData;
        });
    };

    const handleMonthChange = (selectedMonth) => {
        setMes(selectedMonth);
    };

    const handleYearChange = (selectedYear) => {
        setAno(selectedYear);
    };

    useEffect(() => {

    }, [mes, ano]);

    const dataWithDates = combineData(generateDates());
    console.log(dataWithDates);
    return (
        <div>
            <div className="relative">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
                    <div className="flex flex-row">
                        <div className="flex-1">

                            <div className="mb-4">
                                <h3>Selecione o Mês</h3>
                                <div className="flex space-x-2">
                                    {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((month, index) => (
                                        <button
                                            key={index + 1}
                                            className={`px-4 py-2 rounded-lg ${mes === index + 1 ? 'bg-lightBlue-300 text-black' : 'bg-gray-200 text-gray-700'}`}
                                            onClick={() => handleMonthChange(index + 1)}
                                        >
                                            {month}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="mb-4">
                                <h3>Selecione o Ano</h3>
                                <div className="flex space-x-2">
                                    {[2023, 2024, 2025, 2026, 2027].map((year) => (
                                        <button
                                            key={year}
                                            className={`px-4 py-2 rounded-lg ${ano === year ? 'bg-lightBlue-300 text-black' : 'bg-gray-200 text-gray-700'}`}
                                            onClick={() => handleYearChange(year)}
                                        >
                                            {year}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-center flex-none ml-4 mr-4">
                            <div className="mb-4">
                                <h3 className="flex items-center space-x-2 text-lg font-bold">
                                    <div style={{ width: '64px', height: '32px', backgroundColor: 'rgb(255, 235, 168)' }}></div>
                                    <span>Feriados</span>
                                </h3>
                                <h3 className="flex items-center space-x-2 text-lg font-bold">
                                    <div style={{ width: '64px', height: '32px', backgroundColor: 'rgb(251, 199, 194)' }}></div>
                                    <span>Domingos</span>
                                </h3>
                                <h3 className="flex items-center space-x-2 text-lg font-bold">
                                    <div style={{ width: '64px', height: '32px', backgroundColor: 'rgb(212, 235, 255)' }}></div>
                                    <span>Sábados</span>
                                </h3>

                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex">
                    <div className="w-full lg:w-8/12 px-4">
                        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
                            <DataTable
                                className="font-semibold"
                                value={dataWithDates}
                                rows={16}
                                showGridlines
                                stripedRows
                                onRowClick={(e) => handleRowClick(e.data)}
                                rowClassName={rowClassName}
                                loading={loading}
                                paginator
                                responsiveLayout="scroll"
                            >
                                <Column field="date" header="Data" />
                                <Column field="hour_first" header="Início" />
                                <Column field="hour_dinner" header="Almoço" />
                                <Column field="hour_stop" header="Retorno" />
                                <Column field="hour_finish" header="Fim" />
                                <Column field="worked_hours" header="Total" />
                            </DataTable>
                            {selectedData && (
                                <EditHoursDialog
                                    selectedData={selectedData}
                                    onSave={handleSave}
                                    onCancel={handleCancel}
                                />
                            )}
                        </div>
                    </div>
                    <div className="w-full lg:w-4/12 px-4">
                        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-white p-6">
                            <ExportPontos listaDados={dataWithDates} pessoal={id}/>
                        </div>
                        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
                            <h1 className="text-2xl font-bold text-blueGray-700">Horas Trabalhadas</h1>
                            <p className="text-4xl font-bold text-blueGray-700">{calculateTotalWorkedHours(dataWithDates)}</p>
                            <div className="m-4">
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">‎ </h1>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">Horas Extras (220H Obrigatorias)</h1>
                                <p className="text-4xl font-bold text-blueGray-700">
                                    {calculateExtraNormal(dataWithDates)}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">Extras Especias (Domingos e Feriados)</h1>
                                <p className="text-4xl font-bold text-blueGray-700">
                                    {calculateExtraFeriados(dataWithDates)}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">‎ </h1>
                                <p className="text-4xl font-bold text-blueGray-700">
                                    ‎
                                </p>
                                <h1 className="text-2xl font-bold text-blueGray-700">Dias de Folga</h1>
                                <p className="text-4xl font-bold text-blueGray-700">
                                    {calculateDaysOff(dataWithDates)} dias
                                </p>
                                <h1 className="text-sm  font-semibold text-blueGray-400">Referente à {calculateExtraNormal(dataWithDates)}</h1>
                            </div>
                        </div>
                        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white p-6">
                            <h1 className="text-2xl font-bold text-blueGray-700">Informações do Motorista</h1>
                            <div className="m-4">
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">‎ </h1>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">Nome Completo</h1>
                                <p className="text-3xl font-bold text-blueGray-700">
                                    {id.nome}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">Telefone</h1>
                                <p className="text-3xl font-bold text-blueGray-700">
                                    {id.telefone.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">Base</h1>
                                <p className="text-3xl font-bold text-blueGray-700">
                                    {id.base[0].nome}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">CPF</h1>
                                <p className="text-3xl font-bold text-blueGray-700">
                                    {id.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}
                                </p>
                                <h1 className="m-4text-2xl font-bold text-blueGray-700">‎ </h1>
                                <h1 className="text-sm  font-semibold text-blueGray-400">{id.email}</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataTablePontos;